import React, { forwardRef, useState, useImperativeHandle, useCallback, Suspense, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styled from 'styled-components';
import { Tooltip } from 'react-tooltip';
import useOutsideClick from 'utils/useOutsideClick';
import useElementOnScreen from 'utils/useElementOnScreen';
import ToolbarChat from 'components/ToolbarChat';
import ToolbarDropdownMenu from 'components/ToolbarDropdownMenu';
import { toolbarOptions, autocompleteModes } from 'components/ToolbarOptions';
import ToolbarSuggestion from 'components/ToolbarSuggestion';
import ToolbarIcon from 'components/ToolbarIcon';
import ToolbarWriting from 'components/ToolbarWriting';
import ToolbarDropdownAutocompleteModes from './ToolbarDropdownAutocompleteModes';
import { activateItem, generateClause, setHasChanges } from 'redux/toolbar';
import { openModal } from 'redux/modal';
import { modalEvents } from 'utils/useModal';

const AUTOCOMPLETE_MODE_ICONS = {
  inline: 'lizzy-autocomplete-inline',
  dropdown: 'lizzy-autocomplete-dropdown',
  off: 'lizzy-autocomplete-off',
};

const Toolbar = forwardRef((props, ref) => {
  const {
    onOptionClick,
    onClickOutside,
    onRestoreSelection,
    onInsertSuggestion,
  } = props;
  /* Helpers */
  const dispatch = useDispatch();

  /* Selectors*/
  const activeItem = useSelector((state) => state.toolbar.activeItem);
  const isToolbarOpen = useSelector((state) => state.toolbar.isOpen);
  const hasChanges = useSelector((state) => state.toolbar.hasChanges);
  const isExpanded = useSelector((state) => state.toolbar.isExpanded);
  const isWriting = useSelector((state) => state.toolbar.isWriting);
  const selectedText = useSelector((state) => state.toolbar.selectedText);
  const autocompleteMode = useSelector((state) => state.autocomplete.mode);
  const discardChangesModal = useSelector((state) => state.modal.modals.discardChanges);

  /* State */
  const [dropdown, setDropdown] = useState('');
  const [contentComponent, setContentComponent] = useState(null);

  /* Functions */
  const escapeHandler = useCallback(() => {
    if (hasChanges && isToolbarOpen) {
      dispatch(openModal('discardChanges'));
    } else {
      onClickOutside();
    }
  }, [dispatch, hasChanges, isToolbarOpen, onClickOutside]);

  const executeHandler = useCallback((option) => {
    const actionSelectedText = onOptionClick(option);

    option.action && dispatch(option.action(actionSelectedText || selectedText));

    if (option.hasState) {
      dispatch(activateItem(option.key));
      dispatch(setHasChanges(false));
    }

    if(option.dropdownMenu || option.modes) {
      setDropdown(!dropdown ? option.key : '');
    }

    if (option.component) {
      setContentComponent({
        [option.key]: option.component
      });
    }
  }, [dropdown, dispatch, onOptionClick, selectedText]);

  const hasActiveStatus = (option) => {
    return activeItem === option.key;
  };

  const hasOpenedDropdownMenu = (option) => {
    return option.dropdownMenu && option.key === dropdown;
  }

  const hasOpenedModesMenu = useCallback((option) => {
    return option.modes && option.key === dropdown;
  }, [dropdown])

  const assignRefs = (element) => {
    refToolbar.current = element;
    refToolbarResize.current = element;
  };

  const getIcon = (option) => {
    if (option.key === 'autocomplete') {
      return AUTOCOMPLETE_MODE_ICONS[autocompleteMode];
    }

    return option.icon;
  };

  const onModesOptionClick = () => {
    setDropdown('');
  };

  const handleContentSubmit = () => {
    dispatch(generateClause());
  };

  const discardSuggestionHandler = () => {
    onClickOutside();
  };

  const getTooltip = useCallback((option) => {
    const tooltip = option.tooltip;

    if (!tooltip || hasOpenedModesMenu(option)) {
      return null;
    }
    
    const vars = {
      currrentModeTooltip: autocompleteModes.find((mode) => mode.key === autocompleteMode)?.label,
    }

    const tooltipString = tooltip.replace(/{(.*)}/, (match, variable) => {
      return vars[variable];
    });

    return tooltipString;
  }, [autocompleteMode, hasOpenedModesMenu]);

  /* Refs */
  // Expose the following to the parent
  useImperativeHandle(ref, () => ({
    escapeHandler: () => escapeHandler(),
    executeOptionHandler: (option) => executeHandler(option)
  }));

  const refDropdown = useOutsideClick(() => setDropdown(''), ['autocomplete']);
  const refToolbar = useOutsideClick(escapeHandler, ['widget-button']);
  const refToolbarResize = useElementOnScreen();

  /* Effects */
  useEffect(() => {
    const modalCloseHandler = (data) => {
      if (data) {
        onClickOutside();
      } else {
        onRestoreSelection();
      }
    };

    modalEvents.on('closeModal:discardChanges', modalCloseHandler);

    return () => {
      modalEvents.off('closeModal:discardChanges', modalCloseHandler);
    }
  }, [discardChangesModal, onClickOutside, onRestoreSelection]);
  
  /* Render */
  const ActiveComponentContent = activeItem && contentComponent
    ? contentComponent[activeItem]
    : null
  
  const isLoading = isWriting;

  return (
    <Wrapper
      ref={assignRefs}
      isExpanded={isExpanded}
    >
      <Items>
        {toolbarOptions.map((group, groupIndex) => (
          <Group key={`toolbarGroup${groupIndex}`}>
            {group.map((option) => (
              <Item key={option.key}>
                {option.tooltip && (
                  <Tooltip id='toolbar-option' />
                )}
                <Button
                  onClick={() => executeHandler(option) }
                  className={classNames({
                    active: hasActiveStatus(option)
                  })}
                  toolbarKey={option.key}
                  data-tooltip-id='toolbar-option'
                  data-tooltip-content={getTooltip(option)}
                  data-tooltip-delay-show={500}
                  data-exclude-outside-click-id={option.excludeOutsideClickId}
                >
                  <ToolbarIcon
                    name={getIcon(option)}
                    options={option.iconOptions}
                  />
                </Button>

                {hasOpenedModesMenu(option) && (
                  <ToolbarDropdownAutocompleteModes
                    ref={refDropdown}
                    options={option.modes}
                    onOptionClick={onModesOptionClick}
                  />
                )}

                {hasOpenedDropdownMenu(option) && (
                  <ToolbarDropdownMenu
                    ref={refDropdown}
                    options={option.dropdownMenu}
                  />
                )}
              </Item>
            ))}
          </Group>
        ))}
      </Items>

      {ActiveComponentContent && (
        <Suspense fallback={<div />}>
          <ActiveComponentContent
            onSubmit={handleContentSubmit}
          />
        </Suspense>
      )}

      {!ActiveComponentContent && (
        <ToolbarSuggestion
          onInsert={onInsertSuggestion}
          onDiscard={discardSuggestionHandler}
        />
      )}
        
      {!ActiveComponentContent && (
        <Details>
          {isLoading ? <ToolbarWriting /> : <ToolbarChat />}
        </Details>
      )}
    </Wrapper>
  );
});

export default Toolbar;

Toolbar.propTypes = {
  isOpen: PropTypes.bool,
  hasChanges: PropTypes.bool,
  onOptionClick: PropTypes.func,
  onClickOutside: PropTypes.func,
  onRestoreSelection: PropTypes.func,
  onInsertSuggestion: PropTypes.func,
}

Toolbar.defaultProps = {
  onClickOutside: () => {},
  onRestoreSelection: () => {},
  onOptionClick: () => {},
  onInsertSuggestion: () => {},
};

const Wrapper = styled.div`
  cursor: default;
`;

const Button = styled.button`
  all: unset;
  width: 36px;
  height: 36px;
  cursor: pointer;
  border-radius: 7px;
  display: flex;
  justify-content: center;
  align-items: center;

  i {
    font-size: 24px;
  }

  &.active {
    color: var(--lizzy-blue);
    background-color: var(--lizzy-blue-10);
  }

  &:hover {
    background-color: var(--lizzy-black-5);
  }
`;

const Item = styled.div`
  position: relative;

  &:not(:last-child) {
    margin-right: 5px;
  }
`;

const Group = styled.div`
  position: relative;
  display: flex;
  padding-right: 10px;
  flex: 0 1 0%;

  &::after {
    position: absolute;
    content: '';
    right: 5px;
    top: 3px;
    height: 31px;
    width: 1px;
    background-color: var(--lizzy-black-10);
  }

  // The last group
  &:last-child {
    justify-content: space-between;
    align-items: flex-start;
    flex-basis: 100%;
    padding-right: 0;

    // The last item in this group
    ${Item}:last-child {
      margin-left: auto;
    }

    &::after {
      display: none;
    }
  }
`;

const Items = styled.div`
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: flex-start;
`;

const Details = styled.div`
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px #E7E7E7 solid;
`;